import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AppLayoutProviders, SidebarLayout } from '../layout/SidebarLayout';
import { GeneralSettingsTab } from '../components/settings/GeneralSettingsTab';
import { PaymentsSettingsTab } from '../components/settings/PaymentsSettingsTab';

const TABS = [
  { key: 'general', label: 'General' },
  { key: 'payments', label: 'Payments' },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function isValidTab(key: string): key is TabKey {
  return TABS.some((t) => t.key === key);
}

export default function ProfileSettings() {
  const [active, setActive] = useState<TabKey>('general');
  const [searchParams, setSearchParams] = useSearchParams();
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab && isValidTab(tab) && tab !== active) setActive(tab);
  }, [searchParams, active]);

  const setActiveTab = (key: TabKey) => {
    setActive(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    }, { replace: true });
  };

  const onKeyDownTabs = (e: React.KeyboardEvent) => {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    e.preventDefault();
    const keys = TABS.map((t) => t.key);
    let idx = keys.indexOf(active);
    if (e.key === 'ArrowRight') idx = (idx + 1) % keys.length;
    else if (e.key === 'ArrowLeft') idx = (idx - 1 + keys.length) % keys.length;
    else if (e.key === 'Home') idx = 0;
    else if (e.key === 'End') idx = keys.length - 1;
    setActiveTab(keys[idx]);
    tabRefs.current[keys[idx]]?.focus();
  };

  return (
    <AppLayoutProviders>
      <SidebarLayout>
        <div className="max-w-6xl mx-auto px-6 py-8">
          <h1 className="text-2xl font-semibold text-brand-navy mb-6">Settings</h1>
          <div
            role="tablist"
            aria-label="Settings sections"
            onKeyDown={onKeyDownTabs}
            className="flex gap-2 overflow-x-auto pb-2 border-b border-brand-navy/20 mb-8"
          >
            {TABS.map((t) => (
              <button
                key={t.key}
                role="tab"
                ref={(el) => { tabRefs.current[t.key] = el; }}
                aria-selected={active === t.key}
                tabIndex={active === t.key ? 0 : -1}
                onClick={() => setActiveTab(t.key)}
                className={[
                  'px-4 py-2 text-sm font-medium rounded-t whitespace-nowrap focus:outline-none focus:ring-2 focus:ring-brand-accent transition-colors',
                  active === t.key
                    ? 'text-brand-navy border-b-2 border-brand-navy bg-brand-navy/5'
                    : 'text-brand-navy/60 hover:text-brand-navy hover:bg-brand-navy/5',
                ].join(' ')}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div role="tabpanel">
            {active === 'general' && <GeneralSettingsTab />}
            {active === 'payments' && <PaymentsSettingsTab />}
          </div>
        </div>
      </SidebarLayout>
    </AppLayoutProviders>
  );
}
